/**
 * StaffPayslipPage — Sprint 2026-08
 * ----------------------------------------------------------------
 * A print-friendly payslip for one staff × one month. Uses the
 * salary breakdown already returned by the staff summary API
 * (`GET /api/staff/:id/summary?month=YYYY-MM`). No new backend
 * endpoints, no new dependencies.
 *
 * User workflow (payday):
 *   1. Open staff detail → tap "Payslip"
 *   2. New route /staff/:id/payslip?month=YYYY-MM opens the layout
 *   3. Tap "Print / Save as PDF" → browser's print dialog handles
 *      the actual file generation. Mobile browsers offer
 *      "Save as PDF" or "Share as PDF" natively.
 *   4. WhatsApp / email the file to the staff member.
 */
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, fmtMoney, fmtDate } from '../lib/api';
import { currentMonth, labelForMonth } from '../components/MonthPicker';
import './StaffPayslip.css';

export default function StaffPayslipPage() {
    const { staffId } = useParams();
    const [params] = useSearchParams();
    const month = params.get('month') || currentMonth();
    const [staff, setStaff] = useState(null);
    const [summary, setSummary] = useState(null);
    const [businessName, setBusinessName] = useState('');
    const [businessAddress, setBusinessAddress] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [s, sum, prof] = await Promise.all([
                    api.getStaff(staffId),
                    api.getStaffSummary(staffId, { month }),
                    api.getSettings().catch(() => null),
                ]);
                if (cancelled) return;
                setStaff(s);
                setSummary(sum);
                if (prof) {
                    setBusinessName(
                        prof.business_name
                        || prof.legal_name
                        || prof.name
                        || '',
                    );
                    setBusinessAddress(prof.address || prof.business_address || '');
                }
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || 'Could not load payslip');
            }
        })();
        return () => { cancelled = true; };
    }, [staffId, month]);

    if (error) {
        return (
            <div className="payslip-page">
                <p className="section-label">Error</p>
                <p>{error}</p>
                <Link to={`/staff`}>Back to staff</Link>
            </div>
        );
    }

    if (!staff || !summary) {
        return (
            <div className="payslip-page flex items-center justify-center" style={{ minHeight: 320 }}>
                <Loader2 className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    const netPayable = Math.max(0, Number(summary.salary_due || 0) - Number(summary.advances_taken || 0));

    return (
        <div className="payslip-page" data-testid="staff-payslip">
            <div className="actions no-print">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.history.back()}
                    data-testid="payslip-back"
                >
                    <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
                <Button
                    size="sm"
                    onClick={() => window.print()}
                    data-testid="payslip-print"
                >
                    <Printer size={14} className="mr-1" /> Print / Save as PDF
                </Button>
            </div>

            <div className="payslip-header">
                <div>
                    <h1 className="payslip-title">Salary Payslip</h1>
                    <p className="payslip-subtitle">{labelForMonth(month)}</p>
                </div>
                <div className="payslip-meta">
                    <strong>{businessName || 'Your business'}</strong>
                    {businessAddress && (
                        <div style={{ whiteSpace: 'pre-line', marginTop: 2 }}>
                            {businessAddress}
                        </div>
                    )}
                    <div style={{ marginTop: 8 }}>
                        Generated {fmtDate(new Date().toISOString())}
                    </div>
                </div>
            </div>

            <div className="grid-2 section">
                <div>
                    <p className="section-label">Employee</p>
                    <div className="kv" data-testid="payslip-name">
                        <span className="label">Name</span>
                        <span className="value">{staff.name}</span>
                    </div>
                    {staff.phone && (
                        <div className="kv">
                            <span className="label">Phone</span>
                            <span className="value">{staff.phone}</span>
                        </div>
                    )}
                    {staff.joined_at && (
                        <div className="kv">
                            <span className="label">Joined</span>
                            <span className="value">{fmtDate(staff.joined_at)}</span>
                        </div>
                    )}
                </div>
                <div>
                    <p className="section-label">Contract</p>
                    <div className="kv">
                        <span className="label">Monthly base salary</span>
                        <span className="value">{fmtMoney(staff.monthly_salary || 0)}</span>
                    </div>
                    <div className="kv">
                        <span className="label">Days in month</span>
                        <span className="value">{summary.days_in_month || 0}</span>
                    </div>
                    <div className="kv">
                        <span className="label">Per-day salary</span>
                        <span className="value">{fmtMoney(summary.per_day_salary || 0)}</span>
                    </div>
                    <div className="kv">
                        <span className="label">Paid-leave allowance</span>
                        <span className="value">{staff.paid_leave_allowance || 0} / month</span>
                    </div>
                </div>
            </div>

            <div className="section">
                <p className="section-label">Attendance summary</p>
                <div className="kv">
                    <span className="label">Days worked</span>
                    <span className="value">{summary.days_worked || 0}</span>
                </div>
                <div className="kv">
                    <span className="label">Leaves taken</span>
                    <span className="value">{summary.leaves_taken || 0}</span>
                </div>
                {Number(summary.paid_leaves_used || 0) > 0 && (
                    <div className="kv">
                        <span className="label">Paid leaves used (of allowance)</span>
                        <span className="value">{summary.paid_leaves_used}</span>
                    </div>
                )}
                {Number(summary.unpaid_leaves || 0) > 0 && (
                    <div className="kv deduction">
                        <span className="label">Unpaid leaves</span>
                        <span className="value">{summary.unpaid_leaves}</span>
                    </div>
                )}
                {Number(summary.unused_leaves || 0) > 0 && (
                    <div className="kv">
                        <span className="label">Unused allowance (forfeited)</span>
                        <span className="value">{summary.unused_leaves}</span>
                    </div>
                )}
            </div>

            <div className="section">
                <p className="section-label">Earnings</p>
                <div className="kv" data-testid="payslip-base-earned">
                    <span className="label">
                        Base earned ({summary.days_worked || 0} worked{Number(summary.paid_leaves_used || 0) > 0 && ` + ${summary.paid_leaves_used} paid leaves`})
                    </span>
                    <span className="value">{fmtMoney(summary.base_earned || 0)}</span>
                </div>
                {Number(summary.zero_leave_bonus || 0) > 0 && (
                    <div className="kv bonus" data-testid="payslip-bonus">
                        <span className="label">Perfect-attendance bonus</span>
                        <span className="value">+ {fmtMoney(summary.zero_leave_bonus)}</span>
                    </div>
                )}
                <div className="kv total">
                    <span className="label">Salary due</span>
                    <span className="value" data-testid="payslip-salary-due">{fmtMoney(summary.salary_due || 0)}</span>
                </div>
            </div>

            {Number(summary.advances_taken || 0) > 0 && (
                <div className="section">
                    <p className="section-label">Deductions</p>
                    <div className="kv deduction">
                        <span className="label">Advances taken this month</span>
                        <span className="value">− {fmtMoney(summary.advances_taken || 0)}</span>
                    </div>
                </div>
            )}

            <div className="section">
                <p className="section-label">Net payable</p>
                <div className="kv total" data-testid="payslip-net-payable">
                    <span className="label">Amount to pay</span>
                    <span className="value">{fmtMoney(netPayable)}</span>
                </div>
                {Number(summary.salary_paid || 0) > 0 && (
                    <div className="kv" style={{ marginTop: 6 }}>
                        <span className="label">Already paid this month</span>
                        <span className="value">{fmtMoney(summary.salary_paid || 0)}</span>
                    </div>
                )}
                {(() => {
                    const remaining = netPayable - Number(summary.salary_paid || 0);
                    if (remaining <= 0.01) return null;
                    return (
                        <div className="kv" style={{ fontWeight: 600, color: '#dc2626' }}>
                            <span className="label">Pending</span>
                            <span className="value">{fmtMoney(remaining)}</span>
                        </div>
                    );
                })()}
            </div>

            <div className="signatures">
                <div className="sig-line">Employer's signature</div>
                <div className="sig-line">Employee's signature</div>
            </div>

            <div className="footer">
                <span>Generated by Ease My Bill</span>
                <span>{staff.name} · {labelForMonth(month)}</span>
            </div>
        </div>
    );
}
